import { Role, type ServerGroup } from '@bartizo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode, type SyntheticEvent } from 'react';
import { Link } from 'react-router';
import {
  createServerGroup,
  deleteServerGroup,
  listServerGroups,
  updateServerGroup,
} from '../api/servers.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { EmptyState } from '../components/EmptyState.js';
import { PageHeader } from '../components/PageHeader.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Card } from '../ui/Card.js';
import { DataTable } from '../ui/DataTable.js';
import { Dialog } from '../ui/Dialog.js';
import { TextField } from '../ui/Field.js';
import { Loading } from '../ui/Skeleton.js';

/**
 * Server groups (knowledge doc sections 7 and 11; P11.5).
 *
 * A group is the middle settings layer and a set of roles its servers take on.
 * Here an admin makes one, renames it, changes its roles, and removes an empty
 * one; which servers are in it is decided on each server's page, and what it
 * changes is on the Settings and Rules pages, both linked from its row.
 */

const errorText = (caught: unknown, fallback: string): string =>
  caught instanceof Error ? caught.message : fallback;

function RolePicker({
  roles,
  onChange,
}: {
  roles: readonly Role[];
  onChange: (roles: Role[]) => void;
}): ReactNode {
  return (
    <fieldset className="fieldset">
      <legend>Roles its servers take on</legend>
      {Role.options.map((role) => (
        <label key={role} className="checkbox">
          <input
            type="checkbox"
            checked={roles.includes(role)}
            onChange={(event) => {
              onChange(
                event.target.checked ? [...roles, role] : roles.filter((each) => each !== role),
              );
            }}
          />
          {role}
        </label>
      ))}
    </fieldset>
  );
}

export function GroupsPage(): ReactNode {
  const queryClient = useQueryClient();
  const groups = useQuery({ queryKey: ['server-groups'], queryFn: listServerGroups });
  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['server-groups'] });
  };

  return (
    <div className="page">
      <PageHeader
        title="Groups"
        description="Servers that share settings and rules. Global defaults apply first, then a server's group, then the server itself."
      />

      <CreateGroupCard onCreated={refresh} />

      <Card title="Groups">
        {groups.isPending && <Loading />}
        {groups.isError && (
          <Alert tone="danger" title="Could not load the groups">
            Try again in a moment.
          </Alert>
        )}
        {groups.data?.length === 0 && (
          <EmptyState title="No groups yet">
            Create one above, then put servers in it from each server&apos;s page or with an
            enrollment token that names it.
          </EmptyState>
        )}
        {groups.data !== undefined && groups.data.length > 0 && (
          <DataTable
            caption="Server groups"
            columns={[
              {
                key: 'name',
                header: 'Name',
                rowHeader: true,
                sortValue: (group) => group.name,
                cell: (group) => group.name,
              },
              {
                key: 'roles',
                header: 'Roles',
                cell: (group) =>
                  group.roles.length === 0 ? (
                    <span className="muted">—</span>
                  ) : (
                    group.roles.join(', ')
                  ),
              },
              {
                key: 'servers',
                header: 'Servers',
                align: 'end',
                sortValue: (group) => group.serverCount,
                cell: (group) => (
                  <Link to={`/servers?group_id=${encodeURIComponent(group.id)}`}>
                    {group.serverCount} server{group.serverCount === 1 ? '' : 's'}
                  </Link>
                ),
              },
              {
                key: 'configure',
                header: 'Configure',
                cell: (group) => (
                  <span className="row">
                    <Link to={`/settings?group=${encodeURIComponent(group.id)}`}>Settings</Link>
                    <Link to={`/rules?group=${encodeURIComponent(group.id)}`}>Rules</Link>
                  </span>
                ),
              },
              {
                key: 'act',
                header: 'Next',
                cell: (group) => <GroupActions group={group} onChanged={refresh} />,
              },
            ]}
            rows={groups.data}
            rowKey={(group) => group.id}
          />
        )}
      </Card>
    </div>
  );
}

function CreateGroupCard({ onCreated }: { onCreated: () => void }): ReactNode {
  const [name, setName] = useState('');
  const [roles, setRoles] = useState<Role[]>([]);

  const create = useMutation({
    mutationFn: () => createServerGroup({ name: name.trim(), roles }),
    onSuccess: () => {
      setName('');
      setRoles([]);
      onCreated();
    },
  });

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();
    if (name.trim() !== '') create.mutate();
  };

  return (
    <Card title="Create a group">
      <form onSubmit={submit} className="stack">
        <TextField
          label="Name"
          maxLength={100}
          value={name}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
        <RolePicker roles={roles} onChange={setRoles} />
        {create.isError && (
          <Alert tone="danger">{errorText(create.error, 'Could not create the group.')}</Alert>
        )}
        <div className="row">
          <Button
            type="submit"
            variant="primary"
            disabled={name.trim() === ''}
            busy={create.isPending}
            busyLabel="Creating…"
          >
            Create group
          </Button>
        </div>
      </form>
    </Card>
  );
}

function GroupActions({
  group,
  onChanged,
}: {
  group: ServerGroup;
  onChanged: () => void;
}): ReactNode {
  const [editing, setEditing] = useState(false);
  const [removing, setRemoving] = useState(false);

  const remove = useMutation({
    mutationFn: () => deleteServerGroup(group.id),
    onSuccess: () => {
      setRemoving(false);
      onChanged();
    },
  });

  return (
    <>
      <span className="row">
        <Button
          variant="ghost"
          onClick={() => {
            setEditing(true);
          }}
        >
          Edit
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            remove.reset();
            setRemoving(true);
          }}
        >
          Remove
        </Button>
      </span>
      {editing && (
        <EditGroupDialog
          group={group}
          onDone={() => {
            setEditing(false);
            onChanged();
          }}
          onCancel={() => {
            setEditing(false);
          }}
        />
      )}
      {removing && (
        <ConfirmDialog
          title={`Remove ${group.name}?`}
          description={
            group.serverCount > 0
              ? `${String(group.serverCount)} server(s) are still in it. Move them to another group from each server's page first.`
              : 'Its settings and rules go with it. The audit log keeps a copy.'
          }
          confirmLabel="Remove group"
          danger
          busy={remove.isPending}
          error={remove.isError ? errorText(remove.error, 'Could not remove it.') : null}
          onConfirm={() => {
            remove.mutate();
          }}
          onCancel={() => {
            setRemoving(false);
          }}
        />
      )}
    </>
  );
}

function EditGroupDialog({
  group,
  onDone,
  onCancel,
}: {
  group: ServerGroup;
  onDone: () => void;
  onCancel: () => void;
}): ReactNode {
  const [name, setName] = useState(group.name);
  const [roles, setRoles] = useState<Role[]>(group.roles);
  const rolesChanged = [...roles].sort().join(',') !== [...group.roles].sort().join(',');
  const nameChanged = name.trim() !== group.name;

  const save = useMutation({
    mutationFn: () =>
      updateServerGroup(group.id, {
        ...(nameChanged ? { name: name.trim() } : {}),
        ...(rolesChanged ? { roles } : {}),
      }),
    onSuccess: onDone,
  });

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();
    if (nameChanged || rolesChanged) save.mutate();
  };

  return (
    <Dialog title={`Edit ${group.name}`} onClose={onCancel}>
      <form onSubmit={submit} className="stack">
        <TextField
          label="Name"
          maxLength={100}
          value={name}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
        <RolePicker roles={roles} onChange={setRoles} />
        {rolesChanged && group.serverCount > 0 && (
          <Alert tone="info">
            Roles turn modules on, so saving builds new settings for {group.serverCount} server
            {group.serverCount === 1 ? '' : 's'}.
          </Alert>
        )}
        {save.isError && <Alert tone="danger">{errorText(save.error, 'Could not save it.')}</Alert>}
        <div className="dialog-actions">
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            type="submit"
            variant="primary"
            disabled={(!nameChanged && !rolesChanged) || name.trim() === ''}
            busy={save.isPending}
            busyLabel="Saving…"
          >
            Save
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
